import React from "react";
import { Modal } from "react-bootstrap";
import dayjs from "dayjs";

import { formatTimeCheckinOut } from "../../../../../helper/attendance_helper";
import Datatable from "../../../../../core/common/dataTable";

interface EditLogEntry {
    edited_by_user_id: number;
    edited_by_name: string;
    edited_by_profile_pic?: string | null;
    edited_at: string;
    field_changed: string;
    value?: string | null;
    // Legacy support
    old_value?: string | null;
    new_value?: string | null;
}

interface EditHistoryModalProps {
    visible: boolean;
    onClose: () => void;
    editLogs: EditLogEntry[];
    employeeName?: string;
    date?: string;
}

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
    visible,
    onClose,
    editLogs,
    employeeName,
    date,
}) => {
    const displayDate = date
        ? dayjs(date).format("DD MMM YYYY (dddd)")
        : "";

    const formatFieldName = (field: string) => {
        // Already formatted from new backend (e.g. "Punch In", "Punch In 2")
        if (field.startsWith("Punch")) return field;
        // Legacy format
        if (field === "punch_in") return "Punch In";
        if (field === "punch_out") return "Punch Out";
        // Handle legacy indexed fields like "punch_in_2"
        const match = field.match(/^punch_(in|out)_(\d+)$/);
        if (match) {
            return `Punch ${match[1] === "in" ? "In" : "Out"} ${match[2]}`;
        }
        return field;
    };

    const getDisplayValue = (log: EditLogEntry) => {
        // New format uses 'value', legacy uses 'new_value'
        const val = log.value !== undefined ? log.value : log.new_value;
        
        if (!val || val === "N/A" || val === "null") {
            return <span className="text-danger fw-medium">Removed</span>;
        }
        
        const formatted = formatTimeCheckinOut(val);
        return formatted ? formatted : <span className="text-danger fw-medium">Removed</span>;
    };

    const getProfilePicUrl = (pic: string | null | undefined) => {
        if (!pic) return null;
        // If it's already a full URL, use as-is
        if (pic.startsWith("http")) return pic;
        // Otherwise construct from base URL
        return pic;
    };

    const column = [
        {
            title: "Field Changed",
            dataIndex: "field_changed",
            key: "field_changed",
            render: (text: string) => formatFieldName(text),
            sorter: (a: any, b: any) => a.field_changed.localeCompare(b.field_changed),
        },
        {
            title: "Value",
            key: "value",
            render: (_: any, record: EditLogEntry) => getDisplayValue(record),
        },
        {
            title: "Edited By",
            dataIndex: "edited_by_name",
            key: "edited_by_name",
            render: (text: string, record: EditLogEntry) => {
                const profilePic = getProfilePicUrl(record.edited_by_profile_pic);
                return (
                    <div className="d-flex align-items-center gap-2">
                        {profilePic ? (
                            <img
                                src={profilePic}
                                alt={text}
                                className="rounded-circle"
                                style={{ width: 28, height: 28, objectFit: "cover" }}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                }}
                            />
                        ) : (
                            <div
                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-bold"
                                style={{ width: 28, height: 28, fontSize: 12 }}
                            >
                                {(text || "?").charAt(0).toUpperCase()}
                            </div>
                        )}
                        <span>{text}</span>
                    </div>
                );
            },
            sorter: (a: any, b: any) => a.edited_by_name.localeCompare(b.edited_by_name),
        },
        {
            title: "Edited At",
            dataIndex: "edited_at",
            key: "edited_at",
            render: (text: string) => dayjs(text).format("DD MMM YYYY, hh:mm A"),
            sorter: (a: any, b: any) => dayjs(a.edited_at).unix() - dayjs(b.edited_at).unix(),
        },
    ];

    return (
        <Modal show={visible} onHide={onClose} centered size="lg" keyboard>
            <Modal.Header closeButton>
                <Modal.Title>Edit History</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {employeeName && (
                    <div className="mb-3">
                        <span className="fw-semibold">{employeeName}</span>
                        {displayDate && (
                            <span className="text-muted ms-2">{displayDate}</span>
                        )}
                    </div>
                )}

                <Datatable
                    columns={column}
                    dataSource={editLogs || []}
                    Selection={false}
                    showPagination={true}
                />
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={onClose}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditHistoryModal;
